import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './AssetsPanel.module.css';
import { apiFetch } from '../api/client';
import { supabase } from '../auth/supabase';

// Kinds are now data-driven from the server (asset_kinds)

export default function AssetsPanel({ patientId }) {
  const [assets, setAssets] = useState([]);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState({}); // kind -> boolean
  const [kinds, setKinds] = useState([]);
  const [preview, setPreview] = useState(null); // {title, url, id}
  // Layout selection removed; use rows-only for now
  const [thumbSrc, setThumbSrc] = useState({}); // id -> objectURL
  const inputsRef = useRef({});
  const getToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

  const load = async () => {
    try {
      const list = await apiFetch(`/patients/${patientId}/assets?signed=0`, {}, getToken);
      setAssets(list);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); }, [patientId]);
  useEffect(() => { (async () => {
    try {
      const ks = await apiFetch('/asset-kinds', {}, getToken);
      setKinds(ks);
    } catch (e) { /* ignore */ }
  })(); }, []);

  const beginUpload = (k) => {
    inputsRef.current[k]?.click();
  };

  // Fetch thumbnail bytes with Authorization in small batches to avoid 429s
  useEffect(() => {
    (async () => {
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const controller = new AbortController();
      try {
        const api = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const list = assets || [];
        const BATCH = 6;
        let out = {};
        for (let i = 0; i < list.length; i += BATCH) {
          const slice = list.slice(i, i + BATCH);
          const entries = await Promise.all(slice.map(async (a) => {
            if (!a || !a.id || !a.thumb_url) return [a?.id, null];
            const url = `${api}${a.thumb_url}`;
            try {
              let res = await fetch(url, { headers, signal: controller.signal });
              if (res.status === 429) {
                await new Promise(r => setTimeout(r, 350));
                res = await fetch(url, { headers, signal: controller.signal });
              }
              if (!res.ok) return [a.id, null];
              const blob = await res.blob();
              return [a.id, URL.createObjectURL(blob)];
            } catch { return [a?.id, null]; }
          }));
          for (const [id, url] of entries) if (id && url) out[id] = url;
          setThumbSrc(prev => ({ ...prev, ...out }));
        }
      } catch { /* ignore */ }
      return () => controller.abort();
    })();
    // revoke on unmount
    return () => { Object.values(thumbSrc).forEach(u => URL.revokeObjectURL(u)); };
  }, [assets]);

  const openPreview = async (asset) => {
    // Show thumb immediately
    const thumbUrl = thumbSrc[asset.id] || '';
    setPreview({ title: asset.kind || 'Asset', url: thumbUrl, id: asset.id, blobUrl: null, loading: true });
    try {
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const api = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const res = await fetch(`${api}/assets/${asset.id}/original`, { headers });
      if (!res.ok) throw new Error('Failed to load original');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreview(prev => prev && prev.id === asset.id ? { ...prev, url, blobUrl: url, loading: false } : prev);
    } catch (e) {
      // keep thumb; mark as not loading to avoid spinner state
      setPreview(prev => prev && prev.id === asset.id ? { ...prev, loading: false } : prev);
    }
  };

  const onFileSelected = async (k, file) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [k]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file, file.name || 'upload');
      fd.append('kind', k);
      const row = await apiFetch(`/patients/${patientId}/assets`, { method: 'POST', body: fd }, getToken);
      setAssets((prev) => [row, ...prev.filter((a) => (a.kind || '').toLowerCase() !== k.toLowerCase())]);
    } catch (e) {
      alert(e.message || 'Upload failed');
    } finally {
      setUploading((prev) => ({ ...prev, [k]: false }));
      if (inputsRef.current[k]) inputsRef.current[k].value = '';
    }
  };

  const doDelete = async (asset) => {
    if (!asset) return;
    const ok = window.confirm('Delete this asset permanently?');
    if (!ok) return;
    try {
      await apiFetch(`/assets/${asset.id}`, { method: 'DELETE' }, getToken);
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
      setPreview(null);
    } catch (e) {
      alert(e.message || 'Failed to delete');
    }
  };

  const grouped = useMemo(() => {
    const list = (kinds && kinds.length ? kinds : []).map(k => ({ name: k.name, required: !!k.is_required, category: (k.category || 'other').toLowerCase() }));
    const groups = {};
    for (const k of list) {
      const cat = k.category;
      if (!groups[cat]) groups[cat] = {};
      const sub = detectSubcategory(k.name, cat);
      if (!groups[cat][sub]) groups[cat][sub] = [];
      groups[cat][sub].push({ name: k.name, required: k.required });
    }
    // Sort keys and kinds for consistent UI
    for (const cat of Object.keys(groups)) {
      for (const sub of Object.keys(groups[cat])) {
        groups[cat][sub].sort((a,b) => a.name.localeCompare(b.name));
      }
    }
    return groups;
  }, [kinds]);

  const fallbackKinds = ['extraoral frontal rest','intraoral frontal','pre-op panoramic radiograph','postop'];
  const listToShow = (kinds && kinds.length ? null : { other: { Uncategorized: fallbackKinds.map(n => ({ name: n, required: true })) } });

  return (
    <div>
      {error && <div className={styles.error}>{error}</div>}
      {/* Rows-only layout */}

      {Object.entries(listToShow || grouped).map(([category, subgroups]) => (
        <div key={category} className={`${styles.section} ${styles['section-' + (category || 'other')]}`}>
          <div className={styles.sectionTitle}>{titleCase(category)}</div>
          {
          Object.entries(subgroups).map(([sub, names]) => (
            <div key={sub}>
              <div className={styles.subTitle}>{titleCase(sub)}</div>
              <div className={styles.grid}>
              {names.map((entry) => {
              const k = entry.name;
              const found = assets.find((a) => (a.kind || '').toLowerCase() === (k || '').toLowerCase());
              return (
                <div key={k} className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}>{k}</div>
                    <div className={`${styles.badge} ${found ? styles.badgeOk : entry.required ? styles.badgeRequired : ''}`}>{found ? 'Uploaded' : entry.required ? 'Required' : 'Optional'}</div>
                  </div>
                  {found ? (
                    <div className={styles.asset}>
                      <img alt={k} src={thumbSrc[found.id] || ''} loading="lazy" className={styles.thumb} onClick={() => openPreview(found)} />
                    </div>
                  ) : (
                    <div className={styles.placeholder}>{uploading[k] ? 'Uploading…' : 'Upload required asset'}</div>
                  )}
                  <div className={styles.cardActions}>
                    {found && <button className={styles.btn} onClick={() => setPreview({ title: k, url: `/assets/${found.id}/download`, id: found.id })}>Preview</button>}
                    <button className={styles.btnPrimary} onClick={() => beginUpload(k)}>{found ? 'Replace' : 'Upload'}</button>
                    {found && <button className={styles.btn} onClick={() => doDelete(found)}>Delete</button>}
                  </div>
                  <input
                    ref={(el) => (inputsRef.current[k] = el)}
                    type="file"
                    accept="image/*,.dng,.raw,.nef,.cr2,.arw,.heic,.jpg,.jpeg,.png,.webp"
                    style={{ display: 'none' }}
                    onChange={(e) => onFileSelected(k, e.target.files?.[0] || null)}
                  />
                </div>
              );
              })}
              </div>
            </div>
          ))
          }
        </div>
      ))}

      {preview && (
        <div className={styles.modalOverlay} onClick={() => setPreview(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTitle}>{preview.title}</div>
            <img src={preview.url} alt={preview.title} className={styles.modalImg} />
            <div className={styles.cardActions}>
              <button className={styles.btn} onClick={() => { if (preview.blobUrl) URL.revokeObjectURL(preview.blobUrl); setPreview(null); }}>Close</button>
              {!preview.loading && <a className={styles.btn} href={preview.url} target="_blank" rel="noreferrer">Open</a>}
              <button className={styles.btnPrimary} onClick={() => { setPreview(null); beginUpload(preview.title); }}>Replace</button>
              <button className={styles.btn} onClick={() => doDelete(assets.find(a => a.id === preview.id))}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function titleCase(s) {
  if (!s) return '';
  return s.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase());
}

function detectSubcategory(name, category) {
  const n = (name || '').toLowerCase();
  const cat = (category || '').toLowerCase();
  if (cat === 'photo') {
    if (n.startsWith('extraoral')) return 'extraoral';
    if (n.startsWith('intraoral')) return 'intraoral';
    return 'other';
  }
  if (cat === 'radiograph') {
    if (n.includes('panoramic') || n.includes('pano')) return 'panoramic';
    if (n.includes('upper anterior')) return 'upper anterior';
    if (n.includes('lower anterior')) return 'lower anterior';
    if (n.includes('right side')) return 'right side';
    if (n.includes('left side')) return 'left side';
    return 'other';
  }
  if (cat === 'cast') return 'mounted casts';
  return 'other';
}
